import { api } from "@/redux/api";

import {
	AdjustIcpResponse,
	AdjustIcpResponseSchema,
	IcpInfo,
	IcpInfoSchema,
	IcpMessage,
	IcpMessageSchema,
	ListRecommendations,
	ListRecommendationsSchema,
	PrimingInfo,
	PrimingInfoSchema,
	ReferenceCustomersResponse,
	ReferenceCustomersResponseSchema,
	SetReferenceCustomers,
	SetReferenceCustomersSchema,
	SubmitPrimingAnswer,
	SubmitPrimingAnswerSchema,
} from "@/pages/Private/pages/Onboarding/schema/icp";
import {
	CreateTrackingRequest,
	CreateTrackingRequestSchema,
	CreateTrackingResponse,
	CreateTrackingResponseSchema,
} from "@/pages/Private/pages/Onboarding/schema/track";

export const onboardingIcpApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// Tagged so the lists step can read the ICP straight from the chat's cache
		// (`preferCacheValue`) without ever serving a pre-adjustment copy.
		getOnboardingIcp: builder.query<IcpInfo, void>({
			query: () => ({
				url: "onboarding-icp",
				responseSchema: IcpInfoSchema,
			}),
			providesTags: ["OnboardingIcp"],
		}),
		submitIcpHint: builder.mutation<IcpInfo, IcpMessage>({
			query: (body) => ({
				url: "onboarding-icp/hint",
				method: "POST",
				body,
				requestSchema: IcpMessageSchema,
				responseSchema: IcpInfoSchema,
			}),
			invalidatesTags: ["OnboardingIcp"],
		}),
		adjustIcp: builder.mutation<AdjustIcpResponse, IcpMessage>({
			query: (body) => ({
				url: "onboarding-icp/adjust",
				method: "POST",
				body,
				requestSchema: IcpMessageSchema,
				responseSchema: AdjustIcpResponseSchema,
			}),
			invalidatesTags: ["OnboardingIcp"],
		}),
		confirmIcp: builder.mutation<IcpInfo, void>({
			query: () => ({
				url: "onboarding-icp/confirm",
				method: "POST",
				responseSchema: IcpInfoSchema,
			}),
			invalidatesTags: ["OnboardingIcp"],
		}),
		submitReferenceCustomers: builder.mutation<ReferenceCustomersResponse, IcpMessage>({
			query: (body) => ({
				url: "onboarding-icp/reference-customers",
				method: "POST",
				body,
				requestSchema: IcpMessageSchema,
				responseSchema: ReferenceCustomersResponseSchema,
			}),
			invalidatesTags: ["OnboardingIcp"],
		}),
		setReferenceCustomers: builder.mutation<ReferenceCustomersResponse, SetReferenceCustomers>({
			query: (body) => ({
				url: "onboarding-icp/reference-customers",
				method: "PUT",
				body,
				requestSchema: SetReferenceCustomersSchema,
				responseSchema: ReferenceCustomersResponseSchema,
			}),
			invalidatesTags: ["OnboardingIcp"],
		}),
		getListRecommendations: builder.query<ListRecommendations, void>({
			query: () => ({
				url: "onboarding-icp/list-recommendations",
				responseSchema: ListRecommendationsSchema,
			}),
		}),
		createTracking: builder.mutation<CreateTrackingResponse, CreateTrackingRequest>({
			query: (body) => ({
				url: "onboarding-track",
				method: "POST",
				body,
				requestSchema: CreateTrackingRequestSchema,
				responseSchema: CreateTrackingResponseSchema,
			}),
		}),
		getOnboardingPriming: builder.query<PrimingInfo, void>({
			query: () => ({
				url: "onboarding-priming",
				responseSchema: PrimingInfoSchema,
			}),
		}),
		prewarmPrimingDrafts: builder.mutation<void, void>({
			query: () => ({
				url: "onboarding-priming/prewarm",
				method: "POST",
			}),
		}),
		submitPrimingAnswer: builder.mutation<PrimingInfo, SubmitPrimingAnswer>({
			query: (body) => ({
				url: "onboarding-priming/answer",
				method: "POST",
				body,
				requestSchema: SubmitPrimingAnswerSchema,
				responseSchema: PrimingInfoSchema,
			}),
		}),
		confirmPriming: builder.mutation<PrimingInfo, void>({
			query: () => ({
				url: "onboarding-priming/confirm",
				method: "POST",
				responseSchema: PrimingInfoSchema,
			}),
		}),
	}),
});

export const {
	useLazyGetOnboardingIcpQuery,
	useSubmitIcpHintMutation,
	useAdjustIcpMutation,
	useConfirmIcpMutation,
	useCreateTrackingMutation,
	useSubmitReferenceCustomersMutation,
	useSetReferenceCustomersMutation,
	useLazyGetListRecommendationsQuery,
	useLazyGetOnboardingPrimingQuery,
	usePrewarmPrimingDraftsMutation,
	useSubmitPrimingAnswerMutation,
	useConfirmPrimingMutation,
} = onboardingIcpApi;
